import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, logout } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { apiUrl } from "../../utils/apiUrl";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import Swal from "sweetalert2";

function safeRedirectUrl(url) {
  const fallback =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "/") ||
    "/";
  if (!url || typeof url !== "string") return fallback;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return fallback;
  }
}

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData, error: themeError } = useSelector(
    (state) => state.theme
  );

  const adminToken = useSelector(selectAdminToken);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const isFromMobileApp = searchParams.get("fromMobileApp") === "true";
  const mobileAppQuery = isFromMobileApp ? "?fromMobileApp=true" : "";
  const alreadyPlayedHandledRef = useRef(false);

  useEffect(() => {
    const otp = searchParams.get("otp");
    const demo = searchParams.get("demo");
    if (otp || demo) {
      dispatch(logout());
    }
    dispatch(loginUser());
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = safeRedirectUrl(user?.backButtonRedirect);
    const title =
      msg.includes("OTP is required") || msg.includes("No saved user")
        ? "Login required"
        : "Session Expired!";
    Swal.fire(title, msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  // "Already played": if this user already finished the Travel India game with
  // the same session/link, show a notice and send them back. Partial progress
  // still allows resume — only a completed game blocks re-entry.
  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || !user || !adminToken) return;
    if (alreadyPlayedHandledRef.current) return;

    const redirect = safeRedirectUrl(user.backButtonRedirect);

    const showOnceAndRedirect = async () => {
      if (alreadyPlayedHandledRef.current) return;
      alreadyPlayedHandledRef.current = true;
      await Swal.fire({
        title: "You have already played!",
        icon: "error",
        timer: 3000,
        timerProgressBar: true,
        allowOutsideClick: false,
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
      if (isFromMobileApp) {
        window.GameStatus?.postMessage?.("navigate_back");
      } else {
        window.location.href = redirect;
      }
    };

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/gameProgressStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json();
        if (cancelled || alreadyPlayedHandledRef.current) return;
        if (data.success && data.completed) {
          await showOnceAndRedirect();
        }
      } catch (e) {
        console.error("already-played check on login:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, user, adminToken, isAdmin, isFromMobileApp]);

  const retryThemeLoad = () => {
    dispatch(fetchThemeData({ themeId: null }));
  };

  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  if (status === "succeeded" && user && themeStatus === "failed") {
    return (
      <div className="login-main-container">
        <p className="login-theme-error">
          Could not load game theme. Make sure the backend is running on port 5001.
          {themeError ? ` (${themeError})` : ""}
        </p>
        <button type="button" className="begin-play-btn" onClick={retryThemeLoad}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="login-main-container">
      {themeData && (
        <img
          src={s3ThemeAssetUrl(themeData.logo)}
          className="login-logo"
          alt="Game Logo"
        />
      )}
      {user && status === "succeeded" && themeStatus === "succeeded" && themeData && (
        <div>
          <Link
            to={`/travel-game${mobileAppQuery}`}
            className="begin-play-btn"
            style={{
              ...(themeData?.button_backgroundcolor && {
                backgroundColor: themeData.button_backgroundcolor,
              }),
              ...(themeData?.button_color && { color: themeData.button_color }),
              ...(themeData?.button_bordercolor && {
                borderColor: themeData.button_bordercolor,
              }),
              ...(themeData?.button_color && {
                border: `2px solid ${themeData.button_color}`,
              }),
            }}
          >
            Start
          </Link>
        </div>
      )}
    </div>
  );
}

export default Login;
